import { createContext, useContext, useMemo } from "react";
import { useGetCurrentUserUsersCurrentUserGet } from "../api/generated/fastAPI";
import { User } from '../api/models';


type Ctx = {
    user: User | null;
    loading: boolean;
    error: unknown | null;
    refetch: () => void;

    // normalized role sets
    roles: Set<string>;         // union of AC + Airflow
    acRoles: Set<string>;
    airflowRoles: Set<string>;

    // helpers
    hasRole: (...need: string[]) => boolean;      // checks union
    hasAnyRole: (...need: string[]) => boolean;   // checks union
    hasAcRole: (...need: string[]) => boolean;
    hasAnyAcRole: (...need: string[]) => boolean;
    hasAirflowRole: (...need: string[]) => boolean;
    hasAnyAirflowRole: (...need: string[]) => boolean;
};

const CurrentUserContext = createContext<Ctx | null>(null);

// robust role normalization (string | {name:string} | unknown[])
function toRoleSet(value: unknown): Set<string> {
  const out: string[] = [];
  if (Array.isArray(value)) {
    for (const v of value) {
      if (typeof v === "string") out.push(v);
      else if (v && typeof v === "object" && "name" in v && typeof (v as any).name === "string") {
        out.push((v as any).name as string);
      }
    }
  } else if (typeof value === "string") {
    out.push(value);
  }
  return new Set(out.filter(Boolean));
}

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
    // Your generated hook (likely React Query under the hood)
    const { data, isLoading, error, refetch } = useGetCurrentUserUsersCurrentUserGet<User>({
        query: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 60_000,
        },
    });

    const user = (data ?? null) as User | null;

    const acRoles = useMemo(() => toRoleSet(user?.ac_roles as unknown), [user]);
    const airflowRoles = useMemo(() => toRoleSet(user?.airflow_roles as unknown), [user]);
    const roles = useMemo(() => new Set<string>([...acRoles, ...airflowRoles]), [acRoles, airflowRoles]);

    const hasAll = (set: Set<string>, need: string[]) => need.every((r) => set.has(r));
    const hasAny = (set: Set<string>, need: string[]) => need.some((r) => set.has(r));

    const value: Ctx = useMemo(
        () => ({
            user,
            loading: isLoading,
            error: error ?? null,
            refetch: () => void refetch(),

            roles,
            acRoles,
            airflowRoles,

            hasRole: (...need) => hasAll(roles, need),
            hasAnyRole: (...need) => hasAny(roles, need),

            hasAcRole: (...need) => hasAll(acRoles, need),
            hasAnyAcRole: (...need) => hasAny(acRoles, need),

            hasAirflowRole: (...need) => hasAll(airflowRoles, need),
            hasAnyAirflowRole: (...need) => hasAny(airflowRoles, need),
        }),
        [user, isLoading, error, refetch, roles, acRoles, airflowRoles]
    );

    return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser() {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error("useCurrentUser must be used within CurrentUserProvider");
  return ctx;
}
